import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEvent } from '../hooks/useEvent';
import { scoresApi } from '../api/scores';
import { Button } from '../components/Button';
import { strokesToParDelta } from '../utils/format';

export function Scorecard() {
  const { id, subjectId } = useParams<{ id: string; subjectId: string }>();
  const { data: ev } = useEvent(id);
  const { data, isLoading, error } = useQuery({
    queryKey: ['scorecard', id, subjectId],
    queryFn: () => scoresApi.scorecard(id!, subjectId!),
    enabled: !!id && !!subjectId,
  });

  if (isLoading) return <div className="container muted">Loading…</div>;
  if (error || !data) return <div className="container error">Failed to load scorecard</div>;

  const { card } = data;
  const front = card.pars.filter((p) => p.holeNumber <= 9);
  const back = card.pars.filter((p) => p.holeNumber > 9);

  const renderHalf = (label: string, holes: typeof card.pars, total: number) => (
    <div className="stack">
      <h3 style={{ margin: 0 }}>{label}</h3>
      <table className="scorecard">
        <thead>
          <tr>
            <th className="row-label">Hole</th>
            {holes.map((h) => <th key={h.holeNumber}>{h.holeNumber}</th>)}
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="row-label">Par</td>
            {holes.map((h) => <td key={h.holeNumber}>{h.par}</td>)}
            <td><strong>{holes.reduce((a, h) => a + h.par, 0)}</strong></td>
          </tr>
          <tr>
            <td className="row-label">Strokes</td>
            {holes.map((h) => <td key={h.holeNumber}>{h.strokes ?? '—'}</td>)}
            <td><strong>{total || '—'}</strong></td>
          </tr>
          <tr>
            <td className="row-label">+/−</td>
            {holes.map((h) => {
              const d = strokesToParDelta(h.par, h.strokes);
              return <td key={h.holeNumber}><span className={d.className}>{d.label}</span></td>;
            })}
            <td>—</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="container stack">
      <h1>{card.name}</h1>
      <div className="card stack">
        {renderHalf('Front 9', front, card.front9Total)}
        {back.length > 0 && renderHalf('Back 9', back, card.back9Total)}
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <strong>Total strokes: {card.total || '—'}</strong>
          {ev?.event.handicap_enabled && card.net !== null && (
            <strong>Net: {card.net}</strong>
          )}
        </div>
      </div>
      <Link to={`/e/${id}/leaderboard`}><Button variant="secondary" block>Back to leaderboard</Button></Link>
    </div>
  );
}
